import { useState, useEffect, useCallback, useRef } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { UrlGet, confirmacionSMS, confirmacionEmail, UpdatePhone, UpdateEmail, modificarDatosPersonales } from "./method";
import security from "../../assets/svg/pin/cellphone_security.svg";
import Swal from "sweetalert2";
import "../../assets/css/pin.css";
import Encryptor from "../../utils/Encryptor.js";
import ConfigurationManager from "../../settings/ConfigurationManager";

const INITIAL_TIMER = 180;
const RETRY_TIMER = 180;
const MAX_ATTEMPTS = 3;

export default function SecurityCode() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [currentStep, setCurrentStep] = useState("send"); // "send", "validate-phone", "send-email", "validate-email"
    const [currentMethod, setCurrentMethod] = useState("");
    const [numbers, setNumbers] = useState(Array(6).fill(""));
    const [timer, setTimer] = useState(INITIAL_TIMER);
    const [disabled, setDisabled] = useState(false);
    const [message, setMessage] = useState("");
    const [phoneAttemptCount, setPhoneAttemptCount] = useState(0);
    const [emailAttemptCount, setEmailAttemptCount] = useState(0);
    const { id } = useParams();

    const navigate = useNavigate();
    const inputRefs = useRef([]);
    const wrongPinCountRef = useRef(0);

    const formatPhone = (phone) =>
        phone ? `*****${phone.toString().slice(-4)}` : "";

    const formatEmail = (email) => {
        if (!email) return "";
        const firstPart = email.slice(0, 8);
        const hiddenPart = "*".repeat(Math.max(0, email.length - 8));
        return `${firstPart}${hiddenPart}`;
    };



    const fetchData = useCallback(async (forceRefresh = false) => {
        try {
            console.log('Obteniendo datos, forceRefresh:', forceRefresh);
            const crypto = new Encryptor();
            const decrypted = crypto.decrypt(id);
            
            console.log('ID desencriptado:', decrypted);
            
            const response = await UrlGet(decrypted);
            console.log('Datos obtenidos en Send.js:', response);
            console.log('Teléfono actual:', response?.phone);
            console.log('Email actual:', response?.email);
            console.log('updatePhone:', response?.updatePhone);
            console.log('updateMail:', response?.updateMail);
            
            // Si el teléfono tiene 8 dígitos, agregar el 9 al inicio
            let dataWithPhone = response;
            if (response?.phone && response.phone.length === 8) {
                dataWithPhone = {
                    ...response,
                    phone: '9' + response.phone
                };
            }
            
            setData(dataWithPhone);
            return dataWithPhone;
        } catch (error) {
            console.error("Error al obtener los datos:", error);
            Swal.fire("Error", "No se pudo cargar la información.", "error");
        }
    }, [id]);

    useEffect(() => {
        // Forzar datos frescos al cargar la página
        fetchData(true);
    }, [fetchData]);

    // Timer para la validación
    useEffect(() => {
        if (currentStep !== "send" && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else if (timer === 0) {
            setDisabled(true);
        }
    }, [timer, currentStep]);

    const handleRequestCode = async (e) => {
        e.preventDefault();

        if (!data) return;

        setLoading(true);
        try {
            // Usar la URL actual del navegador
            const currentUrl = window.location.href;
            
            if (currentStep === "send") {
                // Siempre validar teléfono primero, independientemente de los flags del backend
                // Enviar SMS primero
                const response = await requestCodeWithUrl(data, 'phone', currentUrl);
                const isSuccess = response.resultado.codigo === "AU00";

                if (!isSuccess) {
                    await Swal.fire({
                        icon: "warning",
                        title: "Error al enviar SMS",
                        text: response.resultado.mensaje,
                        confirmButtonText: "Ok",
                        allowOutsideClick: false,
                        customClass: {
                            confirmButton: "btn btn-success",
                        },
                    });
                    return;
                }

                setPhoneAttemptCount(1);
                setCurrentStep("validate-phone");
                setCurrentMethod("phone");
                setNumbers(Array(6).fill(""));
                setTimer(INITIAL_TIMER);
                setDisabled(false);
                setMessage("");
            } else if (currentStep === "send-email") {
                const response = await requestCodeWithUrl(data, 'email', currentUrl);
                const isSuccess = response.resultado.codigo === "AU00";

                if (!isSuccess) {
                    await Swal.fire({
                        icon: "warning",
                        title: "Error al enviar email",
                        text: response.resultado.mensaje,
                        confirmButtonText: "Ok",
                        allowOutsideClick: false,
                        customClass: {
                            confirmButton: "btn btn-success",
                        },
                    });
                    return;
                }

                setEmailAttemptCount(1);
                setCurrentStep("validate-email");
                setCurrentMethod("email");
                setNumbers(Array(6).fill(""));
                setTimer(INITIAL_TIMER);
                setDisabled(false);
                setMessage("");
            }
        } catch (error) {
            console.error("Error al solicitar código:", error);
            Swal.fire("Error", "No se pudo enviar el código de validación.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (!data || timer !== 0) return;

        const currentAttemptCount = currentMethod === 'phone' ? phoneAttemptCount : emailAttemptCount;
        
        if (currentAttemptCount >= MAX_ATTEMPTS) {
            Swal.fire({
                icon: "warning",
                title: "No pudimos verificar tu dato",
                text: "Superaste el límite establecido de solicitudes de códigos de verificación. Serás dirigido al inicio y deberás repetir el proceso.",
                confirmButtonText: "Volver al inicio",
                allowOutsideClick: false,
                customClass: {
                    confirmButton: "btn btn-outline-primary",
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate("/");
                }
            });
            return;
        }

        setLoading(true);
        try {
            const currentUrl = window.location.href;
            const response = await requestCodeWithUrl(data, currentMethod, currentUrl);
            const isSuccess = response.resultado.codigo === "AU00";

            if (!isSuccess) {
                await Swal.fire({
                    icon: "warning",
                    title: `Error al reenviar código ${currentMethod === 'phone' ? 'SMS' : 'email'}`,
                    text: response.resultado.mensaje,
                    confirmButtonText: "Ok",
                    allowOutsideClick: false,
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                });
                return;
            }

            const newAttemptCount = currentAttemptCount + 1;
            if (currentMethod === 'phone') {
                setPhoneAttemptCount(newAttemptCount);
            } else {
                setEmailAttemptCount(newAttemptCount);
            }

            setTimer(RETRY_TIMER);
            setDisabled(false);
            setNumbers(Array(6).fill(""));
            setMessage("");
            
            await Swal.fire({
                icon: "success",
                title: "Código reenviado",
                text: `Se ha enviado un nuevo código ${currentMethod === 'phone' ? 'SMS' : 'al correo'}.`,
                confirmButtonText: "Ok",
                allowOutsideClick: false,
                customClass: {
                    confirmButton: "btn btn-success",
                },
            });

        } catch (error) {
            console.error("Error al reenviar código:", error);
            Swal.fire("Error", "No se pudo reenviar el código de validación.", "error");
        } finally {
            setLoading(false);
        }
    };

    // Nueva función para enviar código con URL específica
    const requestCodeWithUrl = async (data, step, url) => {
        try {
            console.log(`Enviando código ${step} a URL:`, url);
            
            // Para SMS, enviar el teléfono sin el primer 9 (8 dígitos)
            let phoneToSend = "";
            if (step === 'phone' && data.phone) {
                phoneToSend = data.phone.length === 9 && data.phone.startsWith('9') 
                    ? data.phone.substring(1) 
                    : data.phone;
            }
            
            let requestBody = {
                "rut": data.rut,
                "fono": phoneToSend,
                "mail": step === 'email' ? data.email : "",
                "url": url
            };

            console.log('Request body:', requestBody);

            const response = await fetch(`${ConfigurationManager.API_URL}/SolictaActualizarDatosContacto`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Respuesta envío ${step}:`, result);
            return result;

        } catch (error) {
            console.error(`Error durante request: requestCodeWithUrl (${step})`, error);
            return { 
                resultado: { 
                    codigo: "ERROR", 
                    mensaje: `Error al enviar código ${step === 'phone' ? 'SMS' : 'email'}: ${error.message}` 
                } 
            };
        }
    };

    // Funciones para manejar los códigos
    const handleChange = (index, value) => {
        if (/^[a-zA-Z0-9\b]*$/.test(value)) {
            const updatedNumbers = [...numbers];
            updatedNumbers[index] = value;
            setNumbers(updatedNumbers);

            // Si hay valor, mover foco al siguiente input
            if (value && index < 5) {
                inputRefs.current[index + 1].focus();
            }
        }
    };

    const handleFocus = (index) => {
        const updatedNumbers = [...numbers];
        updatedNumbers[index] = "";
        //setNumbers(updatedNumbers); // Opcional: limpiar al hacer foco
    };

    // Validar PIN cuando se complete
    useEffect(() => {
        async function validatePin() {
            const pin = numbers.join("");

            // Solo validar si:
            // 1. El PIN tiene 6 dígitos
            // 2. No estamos en el paso de envío
            // 3. Hay al menos un dígito que no esté vacío (para evitar validaciones automáticas)
            if (pin.length === 6 && currentStep !== "send" && numbers.some(num => num !== "")) {
                console.log(`Validando PIN: ${pin} para método: ${currentMethod}`);
                const currentUrl = window.location.href;
                console.log('URL de validación:', currentUrl);

                let response = null;
                try {
                    if (currentMethod === "phone") {
                        console.log('Validando SMS...');
                        response = await confirmacionSMS(currentUrl, pin);
                        console.log('Respuesta SMS:', response);
                        if (response?.resultado?.codigo === "AU00") {
                            console.log('SMS validado correctamente, actualizando phone...');
                            await UpdatePhone(id, false);
                        }
                    } else if (currentMethod === "email") {
                        console.log('Validando Email...');
                        response = await confirmacionEmail(currentUrl, pin);
                        console.log('Respuesta Email:', response);
                        if (response?.resultado?.codigo === "AU00") {
                            console.log('Email validado correctamente, actualizando email...');
                            await UpdateEmail(id, false);
                        }
                    }

                    // Si la respuesta no existe o hay error en la API
                    if (!response) {
                        console.error('No se recibió respuesta del servidor');
                        setMessage("Error de conexión. Intenta nuevamente.");
                        return;
                    }

                    console.log('Código de respuesta:', response.resultado?.codigo);

                    // Si la validación del PIN fue exitosa
                    if (response.resultado?.codigo === "AU00") {
                        console.log('Validación exitosa, obteniendo datos actualizados...');
                        const crypto = new Encryptor();
                        const decryptedId = crypto.decrypt(id);
                        let datos = await UrlGet(decryptedId);
                        console.log('Datos actualizados:', datos);
                        
                        // Si el teléfono tiene 8 dígitos, agregar el 9 al inicio
                        if (datos?.phone && datos.phone.length === 8) {
                            datos = {
                                ...datos,
                                phone: '9' + datos.phone
                            };
                        }
                        
                        // Actualizar el estado local con los datos frescos
                        setData(datos);
                        setMessage("El código ingresado es correcto.");
                        setTimer(0);

                        if (currentMethod === "phone") {
                            console.log('Procesando validación de teléfono...');
                            
                            // Siempre proceder con validación de email después del teléfono
                            Swal.fire({
                                icon: "success",
                                title: "¡Teléfono confirmado!",
                                text: "Ahora procederemos con la confirmación del correo electrónico.",
                                confirmButtonText: "Continuar",
                                allowOutsideClick: false,
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    console.log('Usuario confirmó continuar con email...');
                                    setCurrentStep("send-email");
                                    setCurrentMethod("");
                                    setNumbers(Array(6).fill(""));
                                    setTimer(INITIAL_TIMER);
                                    setDisabled(false);
                                    setMessage("");
                                    wrongPinCountRef.current = 0;
                                }
                            });
                        } else if (currentMethod === "email") {
                            console.log('Validación de email completada, guardando datos en .NET...');
                            
                            try {
                                // Ahora sí guardar los datos en .NET
                                const responseModificar = await modificarDatosPersonales(datos);
                                console.log('Respuesta modificarDatosPersonales:', responseModificar);
                                
                                const resultData = responseModificar?.resultado?.wmModificarDatosPersonalesResponse?.wmModificarDatosPersonalesResult?.diffgram?.NewDataSet?.DATA;
                                
                                if (resultData?.HEADER === "AU00") {
                                    console.log('Datos guardados exitosamente en .NET');
                                    
                                    Swal.fire({
                                        icon: "success",
                                        title: "¡Éxito!",
                                        text: "Tus datos han sido actualizados y confirmados correctamente.",
                                        confirmButtonText: "Continuar",
                                        allowOutsideClick: false,
                                    }).then((result) => {
                                        if (result.isConfirmed) {
                                            console.log('Navegando al resumen...');
                                            navigate("/paet/resumen");
                                        }
                                    });
                                } else {
                                    console.error('Error al guardar datos en .NET:', resultData?.MESSAGE);
                                    Swal.fire({
                                        icon: "warning",
                                        title: "Error al guardar",
                                        text: resultData?.MESSAGE || "No se pudieron guardar los datos. Intenta nuevamente.",
                                        confirmButtonText: "Ok",
                                        allowOutsideClick: false,
                                    });
                                }
                            } catch (error) {
                                console.error('Error al llamar modificarDatosPersonales:', error);
                                Swal.fire({
                                    icon: "error",
                                    title: "Error",
                                    text: "Hubo un problema al guardar los datos. Intenta nuevamente.",
                                    confirmButtonText: "Ok",
                                    allowOutsideClick: false,
                                });
                            }
                        }
                    } else {
                        console.log('PIN incorrecto, código:', response.resultado?.codigo);
                        wrongPinCountRef.current += 1;
                        
                        if (wrongPinCountRef.current >= MAX_ATTEMPTS) {
                            Swal.fire({
                                icon: "warning",
                                title: "No pudimos verificar tu dato",
                                text: "Superaste el límite establecido de intentos para ingresar el código. Serás dirigido al inicio y deberás repetir el proceso.",
                                confirmButtonText: "Volver al inicio",
                                allowOutsideClick: false,
                                customClass: {
                                    confirmButton: "btn btn-outline-primary",
                                },
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    navigate("/pin");
                                }
                            });
                            return;
                        }
                        
                        setMessage("El código ingresado es incorrecto, por favor intenta nuevamente");
                        setNumbers(Array(6).fill(""));
                        if (inputRefs.current[0]) {
                            inputRefs.current[0].focus();
                        }
                    }
                } catch (error) {
                    console.error("Error durante la validación:", error);
                    setMessage("Error de conexión. Intenta nuevamente.");
                    setNumbers(Array(6).fill(""));
                    if (inputRefs.current[0]) {
                        inputRefs.current[0].focus();
                    }
                }
            }
        }

        validatePin();
    }, [numbers, currentStep, currentMethod, id, navigate, data]);

    // Función para obtener el título del paso actual
    const getCurrentStepTitle = () => {
        if (currentStep === "send") {
            // Siempre mostrar que se validarán ambos
            return "Código al correo y al teléfono";
        } else if (currentStep === "validate-phone") {
            return data?.updateMail ? "Primer paso: Código al teléfono" : "Código al teléfono";
        } else if (currentStep === "send-email") {
            return "Segundo paso: Código al correo";
        } else if (currentStep === "validate-email") {
            return data?.updatePhone ? "Segundo paso: Código al correo" : "Código al correo";
        }
        return "";
    };

    // Función para obtener la leyenda del paso actual
    const getCurrentLeyenda = () => {
        if (currentStep === "send") {
            // Siempre mostrar que se enviará al teléfono primero
            return (
                <p>
                    <strong>
                        Enviaremos el primer código al número de teléfono {formatPhone(data?.phone)}.
                    </strong>
                </p>
            );
        } else if (currentStep === "send-email") {
            return (
                <p>
                    <strong>
                        Enviaremos el segundo código al correo {formatEmail(data?.email)}.
                    </strong>
                </p>
            );
        } else if (currentStep === "validate-phone") {
            const stepText = data?.updateMail ? "primer código" : "código";
            return (
                <p>
                    <strong>
                        Hemos enviado el {stepText} al número de teléfono {formatPhone(data?.phone)}.
                    </strong>
                </p>
            );
        } else if (currentStep === "validate-email") {
            const stepText = data?.updatePhone ? "segundo código" : "código";
            return (
                <p>
                    <strong>
                        Hemos enviado el {stepText} al correo {formatEmail(data?.email)}.
                    </strong>
                </p>
            );
        }
        return null;
    };

    return (
        <div className="container">
            <div className="card formulario pin mt-5 mb-5">
                <div className="card-header">
                    <div className="form-row">
                        <div className="titulo">
                            Formulario de Retiro de Pensión Anticipada por Enfermedad Terminal
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="subtitulo mt-3">Confirmación datos de contacto</div>
                    </div>
                    <div className="form-row">
                        <div className="subtitulo-2 mt-3">{getCurrentStepTitle()}</div>
                    </div>
                </div>
                <div className="card-body">
                    {currentStep === "send" || currentStep === "send-email" ? (
                        // Vista de solicitud de código
                        <Form onSubmit={handleRequestCode}>
                            <div className="form-row">
                                <div className="form-group col-md-12 text-center">
                                    <img src={security} height={280} alt="cellphone security" />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-12 mt-5">
                                    <div className="body-text">
                                        <span>
                                            Para confirmar tus datos de contacto,{" "}
                                            te enviaremos un código de verificación por
                                            <strong> SMS al teléfono y otro al correo electrónico</strong>.
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {getCurrentLeyenda() && (
                                <div className="form-row">
                                    <div className="form-group col-md-12 mt-4 body-text">
                                        <strong>{getCurrentLeyenda()}</strong>
                                    </div>
                                </div>
                            )}
                            <div className="mt-4 text-center">
                                <div className="form-group mb-2">
                                    <Button
                                        variant="success"
                                        className="col-md-12"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        {loading ? <Spinner animation="border" variant="light" size="sm" /> : "Solicitar código"}
                                    </Button>
                                </div>
                                <div className="form-group mb-2">
                                    <Button
                                        variant="link"
                                        className="col-md-12"
                                        onClick={() => navigate("/pin")}
                                    >
                                        Volver al paso anterior
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    ) : (
                        // Vista de validación de código
                        <Form>
                            <div className="form-row">
                                <div className="form-group col-md-12 text-center">
                                    <img src={security} height={280} alt="cellphone security" />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group col-md-12 mt-4">
                                    <span className="body-text">
                                        {getCurrentLeyenda()}
                                    </span>
                                </div>
                            </div>

                            {currentMethod === "email" && (
                                <div className="form-row">
                                    <div className="form-group col-md-12 mt-3">
                                        <div className="alert alert-info" role="alert">
                                            Tu código ha sido enviado, en caso de que no recibas tu clave de 6 dígitos en la bandeja de entrada principal, recuerda verificar en los correos no deseados o SPAM.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-group col-md-12 mt-2 text-center">
                                    {timer !== 0 ? (
                                        <div className="alert alert-success" role="alert">
                                            Te restan {timer} segundos.
                                        </div>
                                    ) : (
                                        <div className="alert alert-danger" role="alert">
                                            Tu tiempo ha expirado.
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="text-center">
                                <div className="row g-2">
                                    {numbers.map((number, index) => (
                                        <div className="col-2 col-sm-2 col-md-2" key={index}>
                                            <Form.Control
                                                type="text"
                                                name={index}
                                                className={`form-control-code ${message === '' ? '' : message.includes('incorrecto') ? 'red-Key' : 'green-Key'}`}
                                                maxLength={1}
                                                value={number}
                                                disabled={disabled}
                                                onChange={(e) => handleChange(index, e.target.value)}
                                                onFocus={() => handleFocus(index)}
                                                ref={(el) => inputRefs.current[index] = el}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={`mt-4 text-center ${message.includes("incorrecto") ? "text-danger" : "text-success"}`}>
                                {message}
                            </div>
                            
                            <div className="mt-4 text-center">
                                <div className="form-group mb-2">
                                    <Button
                                        variant="success"
                                        className="col-md-12"
                                        onClick={handleResendCode}
                                        disabled={timer !== 0 || loading}
                                    >
                                        {loading ? (
                                            <Spinner animation="border" variant="light" size="sm" />
                                        ) : (
                                            "Solicitar código"
                                        )}
                                    </Button>
                                </div>
                                <div className="form-group mb-2">
                                    <Button
                                        variant="link"
                                        className="col-md-12"
                                        onClick={() => navigate("/pin")}
                                    >
                                        Volver al paso anterior
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </div>
            </div>
        </div>
    );
}
